/**
 * Conversion helpers between QueryCondition[] and PQuery storage format.
 *
 * PQuery storage format (on VespaRecommendationConfiguration.filters):
 *   { "and": [ {"keywords": {"eq_any": ["NBA", "sports"]}}, {"title": {"not_contains": "Podcast"}} ] }
 *
 * Runtime format: QueryCondition[] from @pushly/pquery.
 *
 * Only pqueryToConditions is used in production — the filter editing UI is
 * now AQE's PQueryBuilder, which serializes/deserializes its own store
 * directly and never needs QueryCondition[] as an intermediate shape for
 * writes. pqueryToConditions is still needed to feed vespa-config-summary.tsx's
 * NLG pipeline, which reads the stored filters to describe them in prose.
 */

import { QueryCondition, ConditionOperatorType, ConditionMeta } from '@pushly/pquery'
import { PQueryDocument } from '@pushly/models/lib/interfaces/pquery-document'
import { VespaFilterField } from './types'

/**
 * Convert PQuery storage format back to QueryCondition[].
 *
 * Parses any `meta` block via ConditionMeta.fromDict() so qualifier/timezone/etc.
 * survive the round-trip instead of being dropped on reload.
 */
export function pqueryToConditions(pquery: PQueryDocument | null | undefined): QueryCondition[] {
    if (!pquery) return []

    const andGroup = (pquery as { and?: unknown }).and
    if (!Array.isArray(andGroup)) return []

    const conditions: QueryCondition[] = []

    for (const entry of andGroup) {
        if (typeof entry !== 'object' || entry === null) continue

        const fieldName = Object.keys(entry)[0]
        if (!fieldName) continue

        const operatorObj = (entry as Record<string, unknown>)[fieldName]
        if (typeof operatorObj !== 'object' || operatorObj === null) continue

        const operatorEntry = operatorObj as Record<string, unknown>
        const operatorKey = Object.keys(operatorEntry).find((k) => k !== 'meta')
        if (!operatorKey) continue

        // Validate field and operator are in our supported set
        if (!Object.values(VespaFilterField).includes(fieldName as VespaFilterField)) continue
        if (!Object.values(ConditionOperatorType).includes(operatorKey as ConditionOperatorType)) continue

        const metaDict = operatorEntry.meta
        const meta =
            typeof metaDict === 'object' && metaDict !== null
                ? ConditionMeta.fromDict(metaDict as Record<string, unknown>)
                : undefined

        conditions.push(
            new QueryCondition({
                property: fieldName,
                operator: operatorKey as ConditionOperatorType,
                value: operatorEntry[operatorKey],
                meta,
            }),
        )
    }

    return conditions
}
